/**
 * Brands API - integrates with optimus.Ink.backend Brand endpoints.
 * Uses getApiBaseUrl from api.js and optional Bearer token for auth.
 */

import { getApiBaseUrl, getBaseHeaders } from "./api";

/**
 * @param {{ accessToken?: string }} options
 * @returns {Record<string, string>}
 */
function getHeaders({ accessToken } = {}) {
    const headers = { ...getBaseHeaders() };
    if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
    }
    return headers;
}

/**
 * Build query string from params object
 * @param {Record<string, string|number|undefined>} params
 * @returns {string}
 */
function buildQuery(params) {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v != null && v !== "") search.set(k, String(v));
    });
    const q = search.toString();
    return q ? `?${q}` : "";
}

/**
 * GET /api/brands
 * @param {{ page?: number, limit?: number, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: { brands: array, total: number }, error?: string }>}
 */
export async function fetchBrands({ page, limit, accessToken } = {}) {
    const base = getApiBaseUrl();
    const q = buildQuery({ page, limit });
    const res = await fetch(`${base}/api/brands${q}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/brands/:slug
 * @param {{ slug: string, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: object, error?: string }>}
 */
export async function fetchBrandBySlug({ slug, accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/brands/${encodeURIComponent(slug)}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/brands/:slug/products
 * @param {{ slug: string, page?: number, limit?: number, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: { products: array, total: number }, error?: string }>}
 */
export async function fetchBrandProducts({
    slug,
    page,
    limit,
    accessToken,
} = {}) {
    const base = getApiBaseUrl();
    const q = buildQuery({ page, limit });
    const res = await fetch(
        `${base}/api/brands/${encodeURIComponent(slug)}/products${q}`,
        {
            method: "GET",
            headers: getHeaders({ accessToken }),
        },
    );
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/brands/:slug/reviews
 * @param {{ slug: string, page?: number, limit?: number, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: { reviews: array, total: number }, error?: string }>}
 */
export async function fetchBrandReviews({
    slug,
    page,
    limit,
    accessToken,
} = {}) {
    const base = getApiBaseUrl();
    const q = buildQuery({ page, limit });
    const res = await fetch(
        `${base}/api/brands/${encodeURIComponent(slug)}/reviews${q}`,
        {
            method: "GET",
            headers: getHeaders({ accessToken }),
        },
    );
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * POST /api/brands/:slug/reviews
 * @param {{ slug: string, rating: number, comment?: string, guestName?: string, guestEmail?: string, guestPhone?: string, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: { success: boolean, message: string }, error?: string }>}
 */
export async function submitBrandReview({
    slug,
    rating,
    comment,
    guestName,
    guestEmail,
    guestPhone,
    accessToken,
} = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(
        `${base}/api/brands/${encodeURIComponent(slug)}/reviews`,
        {
            method: "POST",
            headers: getHeaders({ accessToken }),
            body: JSON.stringify({
                rating,
                comment,
                guestName,
                guestEmail,
                guestPhone,
            }),
        },
    );
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/brands/:slug/products/:productSlugOrId/reviews
 * @param {{ slug: string, productSlugOrId: string, page?: number, limit?: number, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: { reviews: array, total: number }, error?: string }>}
 */
export async function fetchProductReviews({
    slug,
    productSlugOrId,
    page,
    limit,
    accessToken,
} = {}) {
    const base = getApiBaseUrl();
    const q = buildQuery({ page, limit });
    const res = await fetch(
        `${base}/api/brands/${encodeURIComponent(slug)}/products/${encodeURIComponent(productSlugOrId)}/reviews${q}`,
        {
            method: "GET",
            headers: getHeaders({ accessToken }),
        },
    );
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * POST /api/brands/:slug/products/:productSlugOrId/reviews
 * @param {{ slug: string, productSlugOrId: string, rating: number, comment?: string, guestName?: string, guestEmail?: string, guestPhone?: string, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: { success: boolean, message: string }, error?: string }>}
 */
export async function submitProductReview({
    slug,
    productSlugOrId,
    rating,
    comment,
    guestName,
    guestEmail,
    guestPhone,
    accessToken,
} = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(
        `${base}/api/brands/${encodeURIComponent(slug)}/products/${encodeURIComponent(productSlugOrId)}/reviews`,
        {
            method: "POST",
            headers: getHeaders({ accessToken }),
            body: JSON.stringify({
                rating,
                comment,
                guestName,
                guestEmail,
                guestPhone,
            }),
        },
    );
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/shopify/collective/brands/:supplierTag/products
 * Shopify se us supplier (Collective brand) ke products. Pagination: first, after.
 * @param {{ supplierTag: string, first?: number, after?: string, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: { products: array, pagination: object }, error?: string }>}
 */
export async function fetchShopifyCollectiveProducts({
    supplierTag,
    first,
    after,
    accessToken,
} = {}) {
    const base = getApiBaseUrl();
    const params = {};
    if (first != null) params.first = first;
    if (after != null && after !== "") params.after = after;
    const q = buildQuery(params);
    const res = await fetch(
        `${base}/api/shopify/collective/brands/${encodeURIComponent(supplierTag)}/products${q}`,
        { method: "GET", headers: getHeaders({ accessToken }) },
    );
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * POST /api/shopify/products/import/bulk
 * Selected products ko bulk import (auth required). Body: { items: [{ supplierTag, productId?, handle? }] }.
 * @param {{ items: Array<{ supplierTag: string, productId?: string, handle?: string }>, accessToken: string }} params
 * @returns {Promise<{ ok: boolean, data?: object, error?: string }>}
 */
export async function importProductsBulk({ items, accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/shopify/products/import/bulk`, {
        method: "POST",
        headers: getHeaders({ accessToken }),
        body: JSON.stringify({ items }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}
/**
 * POST /api/shopify/checkout/cart
 * Cart items se Shopify checkout create karta hai. Response me checkoutUrl aata hai – redirect ke liye.
 * profileId = designer profile id (jis page se customer shop kar raha hai) – order me designer_profile_id set hota hai.
 * @param {{ items: Array<{ productId: string, quantity: number }>, profileId?: number }} params
 * @returns {Promise<{ ok: boolean, data?: { cartId: string, checkoutUrl: string }, error?: string }>}
 */
export async function createCheckoutCart({ items, profileId } = {}) {
    const base = getApiBaseUrl();
    const body = { items };
    if (profileId != null && profileId > 0) body.profileId = profileId;
    const res = await fetch(`${base}/api/shopify/checkout/cart`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}
